/* Instagram "Follow the Journey" — swipe carousel + auto-scroll.
 *
 * - Manual swipe remains enabled.
 * - Desktop click-and-drag remains enabled.
 * - Dot pagination remains enabled.
 * - Automatically advances every 5 seconds.
 * - Loops back to the first card after the last.
 * - Pauses auto-scroll while the user is interacting.
 * - Idempotent; safe to retry after site-experience/live feed loading.
 */

(() => {
  const unwrap = (feed) => {
    if (feed.dataset.ckCarousel === 'ready') return true;

    const track = feed.querySelector('.ck-marquee-track');
    if (!track) return false;

    const group =
      track.querySelector("[data-ck-marquee-source='true']") ||
      track.querySelector('.ck-marquee-group');

    if (!group) return false;

    const items = [...group.children].filter(
      (el) =>
        el.tagName === 'A' &&
        el.dataset.ckFillCopy !== 'true'
    );

    if (!items.length) return false;

    items.forEach((a) => feed.appendChild(a));

    track.remove();

    feed.classList.remove(
      'ck-marquee-viewport',
      'ck-instagram-marquee'
    );

    feed.dataset.ckCarousel = 'ready';

    return true;
  };

  // --------------------------------------------------
  // Click-and-drag horizontal scrolling for desktop
  // --------------------------------------------------

  const enableDrag = (feed) => {
    if (feed.dataset.ckDrag === 'ready') return;

    feed.dataset.ckDrag = 'ready';

    let down = false;
    let startX = 0;
    let startLeft = 0;
    let moved = false;

    feed.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;

      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = feed.scrollLeft;

      feed.style.cursor = 'grabbing';
    });

    window.addEventListener('pointermove', (e) => {
      if (!down) return;

      const dx = e.clientX - startX;

      if (Math.abs(dx) > 4) {
        moved = true;
      }

      feed.scrollLeft = startLeft - dx;
    });

    window.addEventListener('pointerup', () => {
      if (!down) return;

      down = false;
      feed.style.cursor = 'grab';

      setTimeout(() => {
        moved = false;
      }, 0);
    });

    // Prevent opening the Instagram post when dragging.
    feed.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    feed.addEventListener(
      'dragstart',
      (e) => e.preventDefault()
    );

    feed.style.cursor = 'grab';
  };

  // --------------------------------------------------
  // Dot pagination + Auto Scroll
  // --------------------------------------------------

  const wireDots = (section, feed) => {
    if (feed.dataset.ckDots === 'ready') return;

    const dotsWrap = section.querySelector(
      '[data-instagram-dots], .instagram-journey__pagination'
    );

    if (!dotsWrap) return;

    const cards = [...feed.children].filter(
      (el) => el.tagName === 'A'
    );

    if (cards.length < 2) {
      dotsWrap.hidden = true;
      return;
    }

    feed.dataset.ckDots = 'ready';

    dotsWrap.hidden = false;
    dotsWrap.innerHTML = cards
      .map(() => '<i></i>')
      .join('');

    const dots = [
      ...dotsWrap.querySelectorAll('i')
    ];

    // ----------------------------------------------
    // Calculate distance between cards
    // ----------------------------------------------

    const step = () => {
      const s =
        cards[1].getBoundingClientRect().left -
        cards[0].getBoundingClientRect().left;

      return s > 0
        ? s
        : feed.clientWidth || 1;
    };

    // ----------------------------------------------
    // Active dot
    // ----------------------------------------------

    const setActive = (index) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle(
          'is-active',
          i === index
        );
      });
    };

    // ----------------------------------------------
    // Get current card
    // ----------------------------------------------

    // Dots follow the positions the strip can actually stop at. With several
    // cards in view that is fewer than the cards, so the spare dots are hidden
    // and the last dot lights up when the strip is scrolled to its end.
    const stops = () => {
      const max = feed.scrollWidth - feed.clientWidth;
      if (max <= 2) return 1;
      // Rounded up: a final, shorter step to the end is still a position.
      return Math.min(dots.length, Math.ceil(max / step() - 0.05) + 1);
    };

    const layoutDots = () => {
      const count = stops();
      dots.forEach((dot, i) => {
        dot.style.display = count > 1 && i < count ? '' : 'none';
      });
      return count;
    };

    const activeIndex = () => {
      const count = layoutDots();
      const max = feed.scrollWidth - feed.clientWidth;
      if (max > 2 && feed.scrollLeft >= max - 2) return count - 1;
      return Math.min(count - 1, Math.max(0, Math.round(feed.scrollLeft / step())));
    };

    const getCurrentIndex = () => activeIndex();

    window.addEventListener('resize', () => setActive(activeIndex()));
    window.addEventListener('load', () => setActive(activeIndex()));

    // ----------------------------------------------
    // Scroll listener
    // ----------------------------------------------

    let frame = 0;

    feed.addEventListener(
      'scroll',
      () => {
        if (frame) return;

        frame = requestAnimationFrame(() => {
          frame = 0;

          const index = getCurrentIndex();

          setActive(index);
        });
      },
      { passive: true }
    );

    // ----------------------------------------------
    // Auto Scroll
    // ----------------------------------------------

    const AUTO_SCROLL_DELAY = 5000;

    let autoScrollTimer = null;

    const autoScroll = () => {
      const currentIndex = getCurrentIndex();

      // Wrap after the last position the strip can reach, not the last card.
      const nextIndex =
        currentIndex >= stops() - 1
          ? 0
          : currentIndex + 1;

      setActive(nextIndex);

      feed.scrollTo({
        left: nextIndex * step(),
        behavior: 'smooth'
      });
    };

    const startAutoScroll = () => {
      clearInterval(autoScrollTimer);

      autoScrollTimer = setInterval(
        autoScroll,
        AUTO_SCROLL_DELAY
      );
    };

    const resetAutoScroll = () => {
      startAutoScroll();
    };

    // ----------------------------------------------
    // Dot clicks
    // ----------------------------------------------

    dots.forEach((dot, i) => {
      dot.style.cursor = 'pointer';

      dot.addEventListener('click', () => {
        setActive(i);

        feed.scrollTo({
          left: i * step(),
          behavior: 'smooth'
        });

        // Restart 5-second countdown.
        resetAutoScroll();
      });
    });

    // ----------------------------------------------
    // Pause while touching/swiping
    // ----------------------------------------------

    feed.addEventListener(
      'touchstart',
      () => {
        clearInterval(autoScrollTimer);
      },
      { passive: true }
    );

    feed.addEventListener(
      'touchend',
      () => {
        resetAutoScroll();
      },
      { passive: true }
    );

    // ----------------------------------------------
    // Pause while mouse is over carousel
    // ----------------------------------------------

    feed.addEventListener('mouseenter', () => {
      clearInterval(autoScrollTimer);
    });

    feed.addEventListener('mouseleave', () => {
      resetAutoScroll();
    });

    // ----------------------------------------------
    // Initial state
    // ----------------------------------------------

    feed.scrollLeft = 0;
    setActive(activeIndex());

    // Start auto-scroll.
    startAutoScroll();
  };

  // --------------------------------------------------
  // Setup
  // --------------------------------------------------

  const setup = (section) => {
    const feed = section.querySelector(
      '.instagram-journey__feed'
    );

    if (!feed) return;

    if (unwrap(feed)) {
      enableDrag(feed);
      wireDots(section, feed);
    }
  };

  const run = () => {
    document
      .querySelectorAll('.instagram-journey')
      .forEach(setup);
  };

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run);
  }

  // site-experience builds the marquee after load.
  // Live feed may also rebuild the strip.
  setTimeout(run, 700);
  setTimeout(run, 1600);
})();